import type { z } from 'zod';

import {
  indexSearchSchema,
  listParams,
  type ListParams,
} from '@nucleus-commerce/admin-kit/index-table';

import type { BlockListParams, SectionListParams } from '../api/content-api';
import {
  BLOCK_FILTER_FIELDS,
  BLOCK_SORTS,
  SHARED_BLOCK_FILTER_FIELDS,
} from '../components/block-filters';
import { SECTION_FILTER_FIELDS, SECTION_SORTS } from '../components/section-filters';

/** URL search params of a store's block list; mirrors GET /admin/v1/stores/{storeId}/blocks. */
export const blockListSearchSchema = indexSearchSchema(BLOCK_FILTER_FIELDS, { sort: BLOCK_SORTS });
export type BlockListSearch = z.infer<typeof blockListSearchSchema>;

/** URL search params of the shared block list; mirrors GET /admin/v1/org/blocks. */
export const sharedBlockListSearchSchema = indexSearchSchema(SHARED_BLOCK_FILTER_FIELDS, {
  sort: BLOCK_SORTS,
});
export type SharedBlockListSearch = z.infer<typeof sharedBlockListSearchSchema>;

/**
 * A store list's params as the API takes them: `scope` is one of store, shared or all, so the
 * URL's `store,shared` goes out as `all`. Without a scope the API lists the store's own blocks.
 */
export function blockApiParams(params: ListParams): BlockListParams {
  const { scope, ...rest } = params;
  if (scope === undefined) return rest;
  const scopes = String(scope).split(',');
  return { ...rest, scope: scopes.length > 1 ? 'all' : String(scopes[0]) };
}

/** The store list request for a validated search; the page's params go through the same mapping. */
export function blockListParams(search: BlockListSearch): BlockListParams {
  return blockApiParams(listParams(search, BLOCK_FILTER_FIELDS));
}

/** The shared list request for a validated search. */
export function sharedBlockListParams(search: SharedBlockListSearch): BlockListParams {
  return listParams(search, SHARED_BLOCK_FILTER_FIELDS);
}

/** URL search params of the sections list; mirrors GET /admin/v1/stores/{storeId}/sections. */
export const sectionListSearchSchema = indexSearchSchema(SECTION_FILTER_FIELDS, {
  sort: SECTION_SORTS,
});
export type SectionListSearch = z.infer<typeof sectionListSearchSchema>;

/** The list request for a validated search; the page's `useSearchListState().params` match it. */
export function sectionListParams(search: SectionListSearch): SectionListParams {
  return listParams(search, SECTION_FILTER_FIELDS);
}
